import { useRef, useCallback } from 'react';

/**
 * Prevents rapid state updates that freeze the UI
 * Ultra-conservative for extreme abuse resistance
 * 
 * @param throttleMs - Minimum milliseconds between touch events (default: 350ms = ~3 touches/sec)
 * @returns Function that returns true if the touch should be allowed, false if throttled
 */
export function useThrottledTouch(throttleMs = 350) {
  const lastTouchTimeRef = useRef<number>(0);
  const touchCountRef = useRef<number>(0);
  const windowStartRef = useRef<number>(Date.now());

  const shouldAllowTouch = useCallback((): boolean => {
    const ENV = (import.meta as any).env;
    
    // Only throttle on mobile
    if (ENV?.VITE_APP_IS_WEB === 'true' || ENV?.VITE_APP_IS_WEB === true) {
      return true;
    }

    const now = Date.now();
    
    // Reset counter every 10 seconds
    if (now - windowStartRef.current > 10000) {
      touchCountRef.current = 0;
      windowStartRef.current = now;
    }
    
    // Safety valve: If more than 50 touches in 10 seconds, block temporarily
    if (touchCountRef.current >= 50) {
      console.warn('[Touch] 🚨 SAFETY VALVE TRIGGERED - Too many interactions!', {
        count: touchCountRef.current,
        window: '10 seconds',
        blocking: '2 seconds'
      });
      setTimeout(() => {
        touchCountRef.current = 0;
        windowStartRef.current = Date.now();
        console.log('[Touch] ✅ Safety valve reset');
      }, 2000);
      return false;
    }
    
    const timeSinceLastTouch = now - lastTouchTimeRef.current;
    
    // Normal throttling
    if (timeSinceLastTouch < throttleMs) {
      return false;
    }
    
    lastTouchTimeRef.current = now;
    touchCountRef.current++;
    
    // Log every 10th interaction to track accumulation
    if (touchCountRef.current % 10 === 0) {
      console.log('[Touch] 📊 Interaction count:', touchCountRef.current, 'in current window');
    }
    
    return true;
  }, [throttleMs]);

  return shouldAllowTouch;
}

